import { useCallback } from "react";
import { useRouter } from "solito/router";
import { v4 as uuid } from "uuid";
import { Ambulance as AmbulanceIcon, BadgeDollarSign as BadgeDollarSignIcon, ChevronRight as ChevronRightIcon, CircleHelp as CircleHelpIcon, ClipboardCheck as ClipboardCheckIcon, Gavel as GavelIcon, MapPin as MapPinIcon, Percent as PercentIcon, ShieldCheck as ShieldCheckIcon, Wallet as WalletIcon } from "@tamagui/lucide-icons";
import { type GetPersonalizedPromptsQuery, PromptIcon } from "@medbillai/generated-graphql-types";
import { Avatar, Body1, Icon, type IconComponent, ListItem, SkeletonText, XStack, YStack } from "@medbillai/ui";
import { CaseTypeSchema } from "@medbillai/schemas";
import { issuePreviewUrl } from "@medbillai/utils";
import { analyticsService } from "../../../lib/analytics";
import { useCaseStore } from "../../../lib/caseStore";
import { encodePreviewConfig } from "../../case/hooks/useCaseScreenParams";
import { createSystemMessageEvent, createUserChatEvent } from "../../case/utils/createPreviewEvents";
import { HomeSection } from "./HomeSection";
export type QuickStartPrompt = NonNullable<GetPersonalizedPromptsQuery["getPersonalizedPrompts"]>["prompts"][number];

// The wire carries a semantic PromptIcon; the client owns which glyph renders.
// Exhaustive: a new enum value fails typecheck until mapped.
const PROMPT_ICONS: Record<PromptIcon, IconComponent> = {
  [PromptIcon.DOLLAR]: BadgeDollarSignIcon,
  [PromptIcon.PERCENT]: PercentIcon,
  [PromptIcon.CLIPBOARD_CHECK]: ClipboardCheckIcon,
  [PromptIcon.MAP_PIN]: MapPinIcon,
  [PromptIcon.SHIELD_CHECK]: ShieldCheckIcon,
  [PromptIcon.AMBULANCE]: AmbulanceIcon,
  [PromptIcon.WALLET]: WalletIcon,
  [PromptIcon.GAVEL]: GavelIcon
};

// Skeleton row count sized to a typical prompt list.
const SKELETON_ROW_KEYS = ["row-0", "row-1", "row-2", "row-3", "row-4"];
export type QuickStartSectionProps = {
  loading?: boolean;
  prompts: QuickStartPrompt[];
};
export function QuickStartSection({
  loading = false,
  prompts
}: QuickStartSectionProps) {
  const {
    push
  } = useRouter();
  const setPreviewData = useCaseStore(state => state.setPreviewData);

  // Seed a preview chat from the prompt (user line + canned system response).
  const handlePress = useCallback((prompt: QuickStartPrompt) => {
    analyticsService.trackEvent("CaseIntakeStarted", {
      source: `quick_start: ${prompt.text}`
    });
    const storeKey = uuid();
    const events = [{
      event: createUserChatEvent(prompt.text),
      metadata: {}
    }, {
      event: createSystemMessageEvent(prompt.response),
      metadata: {
        messageId: prompt.id
      }
    }];
    setPreviewData(storeKey, {
      events
    });
    push(issuePreviewUrl({
      config: encodePreviewConfig({
        id: storeKey,
        // A stale bundle can receive enum values newer than itself; fall
        // back to OTHER rather than throwing when the prompt is tapped.
        caseType: CaseTypeSchema.catch("OTHER").parse(prompt.caseType),
        title: prompt.text,
        events
      })
    }));
  }, [push, setPreviewData]);

  // Omit-empty: no prompts means no section.
  if (!loading && prompts.length === 0) {
    return null;
  }
  return <HomeSection title="Quick start" loading={loading} data-sentry-element="HomeSection" data-sentry-component="QuickStartSection" data-sentry-source-file="QuickStartSection.tsx">
      <YStack gap="$sp1" data-sentry-element="YStack" data-sentry-source-file="QuickStartSection.tsx">
        {loading ? SKELETON_ROW_KEYS.map(key =>
      // Mirrors the ListItem row (ListItem's title is string-only, so
      // the skeleton line can't ride inside it).
      <XStack key={key} ai="center" gap="$sp1.5" py="$sp1.5" pr="$sp1.5">
                <Avatar size="small" shape="square" skeleton />
                <YStack flex={1}>
                  <SkeletonText loading chars={24}>
                    <Body1 color="$textDefault" />
                  </SkeletonText>
                </YStack>
                <Icon icon={ChevronRightIcon} size="$iconLarge" color="$iconDefault" />
              </XStack>) : prompts.map(prompt => <ListItem key={prompt.id} testID={`quick-start.item-${prompt.id}`}
      // Rows align flush-left with the section header; rounded to
      // soften the hover/press.
      paddingLeft="$sp0" paddingRight="$sp1.5" borderRadius="$br2" leftElement={<Avatar size="small" shape="square"
      // Fallback despite the exhaustive map: a stale bundle can
      // receive enum values newer than itself.
      icon={PROMPT_ICONS[prompt.icon] ?? CircleHelpIcon} />} title={prompt.text} rightElement={<Icon icon={ChevronRightIcon} size="$iconLarge" color="$iconDefault" />} onPress={() => handlePress(prompt)} />)}
      </YStack>
    </HomeSection>;
}